import React, { useState } from 'react';
import { Popover } from '@mui/material';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PickerColumn, defaultColumnKeys, toggleColumn } from './tableColumns';
import 'resources/styles/analyzer-column-picker.css';

interface ColumnPickerProps<K extends string> {
  columns: readonly PickerColumn<K>[];
  visible: readonly K[];
  onChange: (next: K[]) => void;
  maxVisible?: number;
  /** Columns this table can't show right now, with the reason shown beside them. */
  unavailable?: Partial<Record<K, string>>;
  disabled?: boolean;
}

const ColumnPicker = <K extends string>({
  columns,
  visible,
  onChange,
  maxVisible,
  unavailable,
  disabled,
}: ColumnPickerProps<K>): React.ReactElement => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const shownCount = visible.filter((k) => !unavailable?.[k]).length;
  const atLimit = !!maxVisible && visible.length >= maxVisible;

  return (
    <>
      <button
        type='button'
        className='data-segment-btn acp-trigger'
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={disabled}
        aria-haspopup='menu'
        aria-expanded={!!anchor}
        title='Choose which columns this table shows'
      >
        <ViewColumnOutlinedIcon style={{ fontSize: 16 }} />
        Columns
        <span className='acp-count'>{shownCount}</span>
        <ExpandMoreIcon style={{ fontSize: 16 }} />
      </button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { className: 'acp-menu' } }}
      >
        <div role='menu' aria-label='Columns'>
          {maxVisible && (
            <div className={`acp-limit ${atLimit ? 'is-at-limit' : ''}`}>
              {visible.length} of {maxVisible} selected
              {atLimit && ' — remove one to add another'}
            </div>
          )}
          {columns.map((column, i) => {
            const reason = unavailable?.[column.key];
            const checked = visible.includes(column.key) && !reason;
            const blocked =
              !!reason || (atLimit && !visible.includes(column.key));
            const startsGroup =
              column.group && column.group !== columns[i - 1]?.group;
            return (
              <React.Fragment key={column.key}>
                {startsGroup && <div className='acp-group'>{column.group}</div>}
                <button
                  type='button'
                  role='menuitemcheckbox'
                  aria-checked={checked}
                  className='acp-option'
                  disabled={blocked}
                  title={reason}
                  onClick={() =>
                    onChange(
                      toggleColumn(columns, visible, column.key, maxVisible),
                    )
                  }
                >
                  <span
                    className={`acp-check ${checked ? 'is-checked' : ''}`}
                    aria-hidden='true'
                  >
                    {checked && '✓'}
                  </span>
                  <span className='acp-label'>{column.label}</span>
                  {reason && <span className='acp-reason'>{reason}</span>}
                </button>
              </React.Fragment>
            );
          })}
          <div className='acp-footer'>
            <button
              type='button'
              className='acp-reset'
              onClick={() => onChange(defaultColumnKeys(columns))}
            >
              Reset to default
            </button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default ColumnPicker;
